import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { Campaign, Contact, Tag } from "../types";
import { buildProposalDrafts, fetchMatchingContacts } from "./logic";

const contactLabel = (contact: Contact) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ");

export const ProspectingPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const title = translate("crm.prospecting.title");

  const body = <ProspectingBody />;

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="text-xl font-semibold truncate">{title}</h1>
        </MobileHeader>
        <MobileContent>{body}</MobileContent>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {body}
    </div>
  );
};

ProspectingPage.path = "/prospecting";

const ProspectingBody = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [selectedTagIds, setSelectedTagIds] = useState<Identifier[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    [],
  );

  const { data: tags, isPending: tagsPending } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const tagKey = selectedTagIds.map(String).sort().join(",");
  const contactsQuery = useQuery({
    queryKey: ["prospecting", "contacts", tagKey],
    queryFn: () => fetchMatchingContacts(dataProvider, selectedTagIds),
    enabled: selectedTagIds.length > 0,
  });

  const contacts =
    selectedTagIds.length === 0 ? [] : (contactsQuery.data ?? []);

  const toggleTag = (id: Identifier) => {
    setSelectedContactIds([]);
    setSelectedTagIds((current) =>
      current.some((tagId) => String(tagId) === String(id))
        ? current.filter((tagId) => String(tagId) !== String(id))
        : [...current, id],
    );
  };

  const visibleSelectedIds = selectedContactIds.filter((id) =>
    contacts.some((contact) => String(contact.id) === String(id)),
  );
  const allSelected =
    contacts.length > 0 && visibleSelectedIds.length === contacts.length;
  const selectedContacts = contacts.filter((contact) =>
    visibleSelectedIds.some((id) => String(id) === String(contact.id)),
  );

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-base font-medium">
            {translate("crm.prospecting.tags")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.prospecting.tags_hint")}
          </p>
        </div>
        {tagsPending ? (
          <p className="text-sm text-muted-foreground">
            {translate("ra.page.loading")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags?.map((tag) => {
              const checked = selectedTagIds.some(
                (id) => String(id) === String(tag.id),
              );
              return (
                <label
                  key={tag.id}
                  className="inline-flex items-center gap-2 rounded-md border px-2 py-1 cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleTag(tag.id)}
                    aria-label={tag.name}
                  />
                  <Badge
                    variant="secondary"
                    className="text-black font-normal"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </Badge>
                </label>
              );
            })}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        {selectedTagIds.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.prospecting.select_tags")}
          </p>
        ) : contactsQuery.isPending ? (
          <p className="text-sm text-muted-foreground">
            {translate("ra.page.loading")}
          </p>
        ) : contactsQuery.isError ? (
          <p className="text-sm text-muted-foreground">
            {translate("ra.page.error")}
          </p>
        ) : contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.prospecting.no_contacts")}
          </p>
        ) : (
          <ContactTable
            contacts={contacts}
            selectedIds={visibleSelectedIds}
            allSelected={allSelected}
            onToggleContact={(id) =>
              setSelectedContactIds((current) =>
                current.some((selectedId) => String(selectedId) === String(id))
                  ? current.filter(
                      (selectedId) => String(selectedId) !== String(id),
                    )
                  : [...current, id],
              )
            }
            onToggleAll={() =>
              setSelectedContactIds(
                allSelected ? [] : contacts.map((contact) => contact.id),
              )
            }
          />
        )}
      </section>

      {contacts.length > 0 ? (
        <CreateProposals
          contacts={selectedContacts}
          onCreated={() => setSelectedContactIds([])}
        />
      ) : null}
    </div>
  );
};

const CreateProposals = ({
  contacts,
  onCreated,
}: {
  contacts: Contact[];
  onCreated: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [campaignId, setCampaignId] = useState("");
  const [creating, setCreating] = useState(false);

  const { data: campaigns, isPending } = useGetList<Campaign>("campaigns", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  const activeCampaigns = (campaigns ?? []).filter(
    (campaign) => campaign.archived_at == null,
  );
  const campaign = activeCampaigns.find(
    (item) => String(item.id) === campaignId,
  );
  const drafts = buildProposalDrafts(contacts, campaign?.name ?? "");

  const createProposals = async () => {
    if (!campaign || drafts.length === 0 || creating) return;
    setCreating(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      for (const draft of drafts) {
        const { data: deal } = await dataProvider.create("deals", {
          data: {
            name: draft.name,
            company_id: draft.company_id,
            contact_ids: draft.contact_ids,
            stage: draft.stage,
            amount: 0,
            index: 0,
            expected_closing_date: today,
          },
        });
        try {
          await dataProvider.create("deal_campaigns", {
            data: { deal_id: deal.id, campaign_id: campaign.id },
          });
        } catch (linkError) {
          await dataProvider.delete("deals", {
            id: deal.id,
            previousData: deal,
          });
          throw linkError;
        }
      }
      notify("crm.prospecting.created", {
        type: "success",
        messageArgs: { smart_count: drafts.length },
      });
      onCreated();
      refresh();
    } catch {
      notify("crm.prospecting.create_error", { type: "error" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 max-w-md">
      <label className="flex flex-col gap-1 text-sm font-medium">
        {translate("crm.prospecting.campaign")}
        {isPending ? (
          <span className="font-normal text-muted-foreground">
            {translate("ra.page.loading")}
          </span>
        ) : activeCampaigns.length === 0 ? (
          <span className="font-normal text-muted-foreground">
            {translate("crm.prospecting.no_campaigns")}
          </span>
        ) : (
          <select
            className="border-input h-9 rounded-md border bg-transparent px-3 text-sm font-normal shadow-xs"
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
          >
            <option value="">
              {translate("crm.prospecting.campaign_placeholder")}
            </option>
            {activeCampaigns.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.name}
              </option>
            ))}
          </select>
        )}
      </label>
      {drafts.length > 0 ? (
        <p className="text-sm">
          {translate("crm.prospecting.summary", {
            smart_count: drafts.length,
          })}
        </p>
      ) : null}
      <Button
        type="button"
        disabled={!campaign || drafts.length === 0 || creating}
        onClick={() => {
          void createProposals();
        }}
      >
        {creating
          ? translate("ra.page.loading")
          : translate("crm.prospecting.create")}
      </Button>
    </section>
  );
};

const ContactTable = ({
  contacts,
  selectedIds,
  allSelected,
  onToggleContact,
  onToggleAll,
}: {
  contacts: Contact[];
  selectedIds: Identifier[];
  allSelected: boolean;
  onToggleContact: (id: Identifier) => void;
  onToggleAll: () => void;
}) => {
  const translate = useTranslate();
  const isSelected = (id: Identifier) =>
    selectedIds.some((selectedId) => String(selectedId) === String(id));

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Button type="button" variant="outline" size="sm" onClick={onToggleAll}>
          {translate(
            allSelected
              ? "crm.prospecting.clear_selection"
              : "crm.prospecting.select_all",
          )}
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onToggleAll}
                  aria-label={translate("crm.prospecting.select_all")}
                />
              </th>
              <th className="px-3 py-2 font-medium">
                {translate("crm.prospecting.contact")}
              </th>
              <th className="px-3 py-2 font-medium">
                {translate("crm.prospecting.company")}
              </th>
              <th className="px-3 py-2 font-medium">
                {translate("crm.prospecting.job_title")}
              </th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id} className="border-t">
                <td className="px-3 py-2">
                  <Checkbox
                    checked={isSelected(contact.id)}
                    onCheckedChange={() => onToggleContact(contact.id)}
                    aria-label={contactLabel(contact)}
                  />
                </td>
                <td className="px-3 py-2">{contactLabel(contact)}</td>
                <td className="px-3 py-2">{contact.company_name}</td>
                <td className="px-3 py-2">{contact.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
